import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CaretDown, CaretRight, Check, CircleNotch, FirstAid, Drop, Plant as PlantIcon, Sun, CloudSun, Cloud, CloudRain, Moon, Wind, CheckSquare, ChatCircle, Sparkle, At, Umbrella, SunDim, CloudFog } from "@phosphor-icons/react";
import CareRound from "./CareRound";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { ReminderRow } from "./DueSoon";
import { SeverityDots } from "./SeverityMark";
import { UserAvatar } from "./UserAvatar";
import { isActionable } from "../lib/reminders";
import { api, listOpenCritical, listTasks, updateTask, fileUrl } from "../lib/api";
import { sevMeta, SEVERITY_RANK, EVENT_META, relativeTime } from "../lib/theme";
import { useSite } from "../lib/site";
import { plainText } from "../lib/rich";
import { isStale } from "./WeatherFx";
import ArrivalScene from "./wizard/ArrivalScene";
import { resolveTheme } from "./wizard/sceneHoliday";
import { wxOf } from "./wizard/sceneWeather";
import { skyAt } from "./wizard/sceneSky";

const spring = { type: "spring", stiffness: 320, damping: 32 };
const CARD = "snap-start shrink-0 w-[86%] sm:w-[62%] md:w-auto rounded-3xl p-5 flex flex-col min-h-[184px] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(0,0,0,0.14)]";
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const WX_ICON = { Sunny: Sun, "Partly cloudy": CloudSun, Cloudy: Cloud, Rain: CloudRain, Hail: CloudRain, "Rain and hail": CloudRain, "Clear night": Moon, "Humid night": Moon };

const plantTip = (w) =>
  w.temp_f < 45 ? "Chilly out. Keep tender plants inside and away from drafts." : w.temp_f > 90 ? "Hot today. Check soil moisture, pots dry out fast." : w.uv >= 6 ? "Strong sun. Shade delicate leaves around midday."
    : w.rain_today_in > 0 ? "Rain today. Outdoor plants can skip watering." : w.humidity < 30 ? "Dry air. Tropicals will enjoy a misting." : w.humidity > 80 ? "Very humid. Keep air moving to avoid mold." : "Mild and steady. A good day for routine care.";

function Card({ i, tint, Icon, eyebrow, action, children, testid, fx, tall, night }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 + i * 0.07 }}
      className={`${CARD} relative ${tall ? "min-h-[300px]" : ""} ${night ? "dark text-card-foreground" : ""}`} data-night={night ? "1" : undefined} style={{ background: `linear-gradient(160deg, ${tint}1c, ${tint}06), var(--tile-base)` }} data-testid={testid}>
      {fx}
      <div className="relative flex items-center justify-between gap-3">
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="h-9 w-9 shrink-0 rounded-xl inline-flex items-center justify-center" style={{ background: `${tint}22`, color: tint }}><Icon weight="fill" className="h-[18px] w-[18px]" /></span>
          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap truncate">{eyebrow}</span>
        </span>
        {action}
      </div>
      <div className="relative flex flex-col flex-1">{children}</div>
    </motion.div>
  );
}

const CardLink = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="h-8 px-3 -mr-1 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/60 inline-flex items-center gap-1 transition-colors">{children}<CaretRight className="h-3 w-3" /></Link>
);

function WeatherCard({ w, units, failed }) {
  const clk = useClock();
  const phase = skyAt(clk.now, BLANK, clk.fixed).phase;
  if (!w) return (
    <Card i={0} tint="#B88A4A" Icon={CloudSun} eyebrow="Weather" testid="home-weather">
      <p className="mt-auto text-sm text-muted-foreground" data-testid="weather-fallback">{failed ? "The weather station isn't reporting right now. Check back in a few minutes." : "Connecting to the weather station…"}</p>
    </Card>
  );
  const stale = isStale(w);
  const Icon = WX_ICON[w.condition] || CloudSun;
  const tint = "#B88A4A";
  const metric = units.temp === "c";
  const temp = metric ? ((w.temp_f - 32) * 5) / 9 : w.temp_f;
  const wind = units.wind === "kmh" ? `${Math.round(w.wind_mph * 1.609)} km/h` : `${Math.round(w.wind_mph)} mph`;
  const rain = metric ? `${((w.rain_today_in || 0) * 25.4).toFixed(1)} mm` : `${(w.rain_today_in || 0).toFixed(2)} in`;
  const stat = (I, v, label) => <span className="flex items-center gap-1.5 text-xs text-foreground/75"><I className="h-3.5 w-3.5 shrink-0" /><span className="text-foreground font-semibold tabular-nums">{v}</span>{label}</span>;
  const obs = new Date(w.observed_at);
  const obsText = `${obs.toDateString() === new Date().toDateString() ? "" : `${obs.toLocaleDateString([], { month: "short", day: "numeric" })}, `}${obs.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  const eyebrow = <><span className="sm:hidden">Outside</span><span className="hidden sm:inline">At the Green House</span></>;
  return (
    <Card i={0} tint={tint} Icon={Icon} eyebrow={eyebrow} testid="home-weather" fx={<WeatherScene w={w} still={stale || failed} clk={clk} night={phase === "night"} />} tall night={phase === "night"}
      action={<span className={`text-[11px] tabular-nums text-right whitespace-nowrap shrink-0 ${stale ? "text-[#C77700] dark:text-[#FFB340]" : "text-muted-foreground"}`} title="Tempest weather station" data-testid="weather-observed">{stale ? "Last reading " : <span className="hidden sm:inline">Observed </span>}{obsText}</span>}>
      <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
        <span className="text-4xl sm:text-5xl font-semibold tracking-tight tabular-nums leading-none" data-testid="weather-temp">{Math.round(temp)}°{metric ? "C" : "F"}</span>
        <span className="pb-1 text-base font-medium" data-testid="weather-condition">{w.condition}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 max-w-[17rem]">
        {stat(CloudFog, `${Math.round(w.humidity)}%`, "humidity")}
        <span data-testid="weather-wind">{stat(Wind, wind, w.wind_dir)}</span>
        {stat(Umbrella, rain, "rain")}
        {stat(SunDim, `UV ${Math.round(w.uv || 0)}`, "")}
      </div>
      {stale
        ? <p className="mt-auto rounded-2xl bg-card/85 fine:bg-card/70 fine:backdrop-blur-md px-3 py-2 text-xs text-muted-foreground leading-snug shadow-[0_2px_10px_-4px_rgba(0,0,0,0.2)]" data-testid="weather-stale">The station hasn't sent a new reading in a while, so this may be out of date.</p>
        : <p className="mt-auto rounded-2xl bg-card/85 fine:bg-card/70 fine:backdrop-blur-md px-3 py-2 flex items-start gap-2 text-sm leading-snug shadow-[0_2px_10px_-4px_rgba(0,0,0,0.2)]" data-testid="weather-tip"><PlantIcon weight="fill" className="h-4 w-4 mt-0.5 shrink-0 text-primary" />{plantTip(w)}</p>}
    </Card>
  );
}

const BLANK = ["#000000", "#000000", "#000000"];
function useClock() {
  const q = new URLSearchParams(window.location.search).get("time");
  const fixed = q && /^\d{1,2}:\d{2}$/.test(q) ? q.split(":").reduce((h, m) => +h * 60 + +m) : null;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 5 * 60 * 1000); return () => clearInterval(t); }, []);
  return { now, fixed };
}

function WeatherScene({ w, still, clk, night }) {
  const site = useSite();
  const { now, fixed } = clk;
  const qs = new URLSearchParams(window.location.search);
  const theme = resolveTheme(qs.get("scene") || site.scene_theme, site.scene_holidays);
  const live = qs.get("wx") || wxOf(w.condition, w.temp_f);
  const weather = live === "night" ? "sun" : live;
  const phase = skyAt(now, BLANK, fixed).phase;
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none isolate" data-testid="weather-scene" data-wx={weather} data-phase={phase} data-moving={still ? "0" : "1"}>
      <ArrivalScene ambient band still={still} weather={weather} theme={theme} clock={now} clockMinutes={fixed} name="The Green House" className={night ? "" : "dark:after:absolute dark:after:inset-0 dark:after:bg-black/25"} />
      <div className="absolute inset-0" style={{ background: night ? "linear-gradient(to bottom, hsl(var(--card) / 0.55) 0%, hsl(var(--card) / 0.35) 36%, hsl(var(--card) / 0) 56%)" : "linear-gradient(to bottom, hsl(var(--card) / 0.9) 0%, hsl(var(--card) / 0.78) 36%, hsl(var(--card) / 0.3) 52%, hsl(var(--card) / 0) 64%)" }} />
    </div>
  );
}

const dueLabel = (d) => {
  if (!d) return { text: "No date", late: false };
  const n = Math.round((new Date(d + "T12:00:00") - new Date(new Date().toDateString() + " 12:00")) / 86400000);
  return { text: n < 0 ? `${-n}d overdue` : n === 0 ? "Today" : n === 1 ? "Tomorrow" : `In ${n} days`, late: n < 0 };
};

function TasksCard({ tasks, onDone }) {
  const [busy, setBusy] = useState(null);
  const done = async (t) => {
    setBusy(t.id);
    try { await updateTask(t.id, { status: "done" }); toast.success("Task done"); onDone(); } catch { toast.error("Could not update task"); } finally { setBusy(null); }
  };
  return (
    <Card i={1} tint="#5C7FA8" Icon={CheckSquare} eyebrow="Your tasks" testid="home-tasks" action={<CardLink to="/tasks" testid="home-tasks-all">All</CardLink>}>
      {tasks.length ? (
        <ul className="mt-4 flex flex-col gap-2">
          {tasks.slice(0, 3).map((t) => {
            const d = dueLabel(t.due_date);
            return (
              <li key={t.id} className="flex items-center gap-3 rounded-2xl bg-background/70 dark:bg-background/40 p-2.5 pr-3" data-testid={`home-task-${t.id}`}>
                <button type="button" onClick={() => done(t)} disabled={busy === t.id} aria-label="Mark done" data-testid={`home-task-done-${t.id}`}
                  className="h-7 w-7 rounded-full border-2 border-border inline-flex items-center justify-center shrink-0 text-transparent hover:text-white hover:bg-[#34C759] hover:border-transparent transition-colors">
                  {busy === t.id ? <CircleNotch className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : <Check weight="bold" className="h-3.5 w-3.5" />}
                </button>
                <Link to={t.plant_id ? `/plants/${t.plant_id}?tab=tasks` : "/tasks"} className="min-w-0 flex-1">
                  <span className="block text-sm font-medium truncate">{t.title}</span>
                  <span className="block text-xs text-muted-foreground truncate">{t.plant?.nickname}{t.plant?.nickname && " · "}<span className={d.late ? "text-[#FF453A]" : ""}>{d.text}</span></span>
                </Link>
              </li>
            );
          })}
          {tasks.length > 3 && <li className="text-xs text-muted-foreground px-1">+{tasks.length - 3} more</li>}
        </ul>
      ) : (
        <div className="mt-auto pt-6"><p className="text-lg font-semibold tracking-tight">You're all caught up</p><p className="mt-0.5 text-sm text-muted-foreground">No open tasks assigned to you.</p></div>
      )}
    </Card>
  );
}

const feedText = (i) => (i.kind === "comment" ? (i.mentioned ? "mentioned you on" : "commented on") : i.event_type === "critical" ? "flagged an acute event on" : `${(EVENT_META[i.event_type]?.label || "logged care on").toLowerCase()}`);

function FeedCard({ feed, since }) {
  return (
    <Card i={2} tint="#9A6E8F" Icon={Sparkle} eyebrow={since ? "Since your last visit" : "Recently"} testid="home-feed"
      action={feed?.total > 0 && <span className="h-6 min-w-6 px-2 rounded-full bg-[#9A6E8F]/15 text-[#9A6E8F] text-[11px] font-semibold inline-flex items-center justify-center tabular-nums" data-testid="home-feed-count">{feed.total}</span>}>
      {feed?.items?.length ? (
        <ul className="mt-4 flex flex-col gap-3">
          {feed.items.slice(0, 4).map((i) => (
            <li key={i.id} data-testid={`home-feed-${i.id}`}>
              <Link to={`/plants/${i.plant_id}${i.kind === "comment" ? "?tab=comments" : ""}`} className="flex items-start gap-2.5 group">
                <span className="relative shrink-0">
                  <UserAvatar user={i.author || {}} className="h-7 w-7 text-[10px]" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-background inline-flex items-center justify-center">
                    {i.kind === "comment" ? (i.mentioned ? <At weight="bold" className="h-2.5 w-2.5 text-[#AF52DE]" /> : <ChatCircle weight="fill" className="h-2.5 w-2.5 text-[#AF52DE]" />)
                      : i.event_type === "critical" ? <FirstAid weight="fill" className="h-2.5 w-2.5 text-[#FF3B30]" /> : <Drop weight="fill" className="h-2.5 w-2.5" style={{ color: EVENT_META[i.event_type]?.color }} />}
                  </span>
                </span>
                <span className="min-w-0 text-sm leading-snug">
                  <span className="font-medium">{i.author?.name?.split(" ")[0] || "Someone"}</span> <span className="text-muted-foreground">{feedText(i)}</span> <span className="font-medium group-hover:underline">{i.plant?.nickname}</span>
                  {i.kind === "comment" && <span className="block text-xs text-muted-foreground truncate">“{plainText(i.text)}”</span>}
                  <span className="block text-[11px] text-muted-foreground">{relativeTime(i.created_at)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-auto pt-6"><p className="text-lg font-semibold tracking-tight">All quiet</p><p className="mt-0.5 text-sm text-muted-foreground">No new activity from the team since you were last here.</p></div>
      )}
    </Card>
  );
}

function CaseMini({ c }) {
  const s = sevMeta(c.severity);
  return (
    <Link to={`/cases/${c.id}`} className="flex items-center gap-3 py-2.5 group" data-testid={`today-case-${c.id}`}>
      <span className="h-10 w-10 rounded-xl overflow-hidden bg-muted shrink-0 inline-flex items-center justify-center">
        {c.plant?.photo_path ? <img src={fileUrl(c.plant.photo_path, 120)} alt="" className="h-full w-full object-cover" /> : <PlantIcon weight="duotone" className="h-4 w-4 text-muted-foreground" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate">{c.plant?.nickname}</span>
        <span className="flex items-center gap-1.5 text-xs" style={{ color: s.color }}><SeverityDots level={s.level} color={s.color} size="xs" />{s.label}</span>
      </span>
      <CaretRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}

function ListSheet({ open, onOpenChange, title, sub, children, testid }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl border-0 max-h-[85dvh] overflow-y-auto p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:max-w-lg sm:mx-auto" data-testid={testid}>
        <span aria-hidden className="block mx-auto -mt-1 mb-3 h-1 w-10 rounded-full bg-muted-foreground/25" />
        <SheetHeader className="text-left mb-2"><SheetTitle className="text-xl font-semibold tracking-tight">{title}</SheetTitle><SheetDescription>{sub}</SheetDescription></SheetHeader>
        <ul className="divide-y divide-border/60">{children}</ul>
      </SheetContent>
    </Sheet>
  );
}

const Chip = ({ color, Icon, children, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="h-9 pl-2.5 pr-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-[background-color,transform] active:scale-[0.97]"
    style={{ background: `${color}14`, color }}>
    <Icon weight="fill" className="h-4 w-4" /><span className="text-foreground">{children}</span>
  </button>
);

const LAST_KEY = "gh-last-visit";

export default function HomeHero({ user, greetingText, actions, aside, showToday, version, reminders, onCareChanged }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [feed, setFeed] = useState(null);
  const [weather, setWeather] = useState(null);
  const [wxFailed, setWxFailed] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [round, setRound] = useState(false);
  const site = useSite();
  const showWx = site.show_weather !== false;
  const since = useMemo(() => localStorage.getItem(LAST_KEY), []);
  const [cardsOpen, setCardsOpen] = useState(() => { const v = localStorage.getItem("gh-today-cards"); return v ? v === "1" : window.innerWidth >= 768; });
  const toggleCards = () => setCardsOpen((v) => { localStorage.setItem("gh-today-cards", v ? "0" : "1"); return !v; });

  const loadTasks = () => listTasks({ mine: true, status: "open" }).then((ts) => setTasks([...ts].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999")))).catch(() => {});
  useEffect(() => {
    listOpenCritical().then((xs) => setCases([...xs].sort((a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0)))).catch(() => {});
    loadTasks();
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/feed", { params: since ? { since } : {} }).then((r) => setFeed(r.data)).catch(() => {});
    const t = setTimeout(() => localStorage.setItem(LAST_KEY, new Date().toISOString()), 8000);
    const loadWx = () => api.get("/weather/current").then((r) => { setWeather(r.data); setWxFailed(false); }).catch(() => setWxFailed(true));
    loadWx();
    const wx = setInterval(loadWx, 5 * 60 * 1000);
    return () => { clearTimeout(t); clearInterval(wx); localStorage.setItem(LAST_KEY, new Date().toISOString()); };
  }, [since]);

  const due = reminders.filter(isActionable).sort((a, b) => a.days_until - b.days_until);
  const dueToday = reminders.filter((r) => r.days_until <= 0);
  const plantsToday = new Set(dueToday.map((r) => r.plant_id)).size;
  const refresh = () => { onCareChanged?.(); };

  return (
    <section data-testid="today-card">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={spring} className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 lg:gap-8" data-testid="welcome-section">
        <div className="flex items-center gap-3.5 min-w-0">
          <UserAvatar user={user} className="h-11 w-11 sm:h-12 sm:w-12 text-sm shrink-0 ring-4 ring-background shadow-[0_8px_24px_-12px_rgba(0,0,0,0.35)]" />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="welcome-date">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
            <h1 className="mt-0.5 text-2xl sm:text-3xl font-semibold tracking-tight leading-tight break-words [text-wrap:balance]" data-testid="welcome-greeting">{greetingText}</h1>
          </div>
          {aside}
        </div>
        {actions}
      </motion.div>

      {showToday && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }} className="mt-5 flex flex-wrap items-center gap-2" data-testid="today-headline">
          <span className="text-sm text-muted-foreground mr-1">Your day</span>
          {plantsToday > 0 && <Chip color="#0A84FF" Icon={Drop} onClick={() => setRound(true)} testid="start-round-btn">{plural(plantsToday, "plant")} to care for · Start round</Chip>}
          {!plantsToday && due.length > 0 && <Chip color="#0A84FF" Icon={Drop} onClick={() => setSheet("care")} testid="today-care-chip">{plural(due.length, "care")} coming up</Chip>}
          {cases.length > 0 && <Chip color="#FF3B30" Icon={FirstAid} onClick={() => setSheet("cases")} testid="today-acute-chip">{plural(cases.length, "acute case")}</Chip>}
          {tasks.length > 0 && <Chip color="#2F7DF6" Icon={CheckSquare} onClick={() => navigate("/tasks")} testid="today-tasks-chip">{plural(tasks.length, "task")} for you</Chip>}
          {!plantsToday && !due.length && !cases.length && !tasks.length && <span className="text-sm font-medium" data-testid="today-clear">Nothing urgent. Enjoy the green.</span>}
          <button type="button" onClick={toggleCards} aria-expanded={cardsOpen} data-testid="today-cards-toggle"
            className="ml-auto h-8 pl-3 pr-2.5 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted inline-flex items-center gap-1 transition-colors">
            {cardsOpen ? "Hide details" : "Weather & updates"}<CaretDown weight="bold" className={`h-3 w-3 transition-transform duration-300 ${cardsOpen ? "rotate-180" : ""}`} />
          </button>
        </motion.div>
      )}

      <AnimatePresence initial={false}>
        {(cardsOpen || !showToday) && (
          <motion.div key="cards" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ type: "spring", stiffness: 300, damping: 34 }} className="-mx-6 overflow-hidden">
            <div className={`px-6 pt-4 pb-3 flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:overflow-visible md:grid-cols-2 ${showWx ? "lg:grid-cols-3" : ""}`} data-testid="today-list">
              {showWx && <WeatherCard w={weather} failed={wxFailed} units={{ temp: site.temp_unit, wind: site.wind_unit }} />}
              <TasksCard tasks={tasks} onDone={loadTasks} />
              <FeedCard feed={feed} since={since} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CareRound open={round} onClose={() => setRound(false)} reminders={reminders} onChanged={refresh} />
      <ListSheet open={sheet === "cases"} onOpenChange={(o) => !o && setSheet(null)} title="Acute cases" sub="Most severe first." testid="today-sheet-cases">
        {cases.map((c) => <li key={c.id}><CaseMini c={c} /></li>)}
      </ListSheet>
      <ListSheet open={sheet === "care"} onOpenChange={(o) => !o && setSheet(null)} title="Care coming up" sub="Overdue first. Tap Done to log it." testid="today-sheet-care">
        {due.map((r) => <ReminderRow key={`${r.plant_id}-${r.event_type}`} r={r} onDone={refresh} />)}
      </ListSheet>
    </section>
  );
}
